import React, { useState, useEffect, useRef } from 'react';

// Main App Component
const App = () => {
  // --- State Variables ---
  const [currentStep, setCurrentStep] = useState(1); // Controls the current step of the application
  const [numMembers, setNumMembers] = useState(''); // Total number of members
  const [members, setMembers] = useState([]); // Array to store member details
  const [unassignedMembers, setUnassignedMembers] = useState([]); // Members whose details haven't been entered yet

  const [selectedMemberForDetails, setSelectedMemberForDetails] = useState(''); // Currently selected member for inputting advanced/meals
  const [currentAdvanced, setCurrentAdvanced] = useState(''); // Advanced amount for the selected member
  const [currentMeals, setCurrentMeals] = useState(''); // Total meals for the selected member

  const [numGuestMealMembers, setNumGuestMealMembers] = useState(''); // Number of members with guest meals
  const [guestMealSelectionCount, setGuestMealSelectionCount] = useState(0); // Counter for how many guest meal members have been selected
  const [selectedGuestMembers, setSelectedGuestMembers] = useState({}); // Stores which members are selected for guest meals and their costs

  const [totalBazarCost, setTotalBazarCost] = useState(''); // Total bazaar cost for the month

  // State for other costs and their distribution
  const [maasiCost, setMaasiCost] = useState('');
  const [maasiDistribution, setMaasiDistribution] = useState('divided'); // 'same', 'divided', 'select'
  const [selectedMaasiMembers, setSelectedMaasiMembers] = useState({}); // { memberId: true }

  const [gasCost, setGasCost] = useState('');
  const [gasDistribution, setGasDistribution] = useState('divided');
  const [selectedGasMembers, setSelectedGasMembers] = useState({}); // FIX: Changed from {} to useState({})

  const [othersCost, setOthersCost] = useState('');
  const [othersDistribution, setOthersDistribution] = useState('divided');
  const [selectedOthersMembers, setSelectedOthersMembers] = useState({}); // FIX: Changed from {} to useState({})

  const [wifiBill, setWifiBill] = useState('');
  const [wifiDistribution, setWifiDistribution] = useState('divided');
  const [selectedWifiMembers, setSelectedWifiMembers] = useState({}); // FIX: Changed from {} to useState({})

  const [electricBill, setElectricBill] = useState('');
  const [electricDistribution, setElectricDistribution] = useState('divided');
  const [selectedElectricMembers, setSelectedElectricMembers] = useState({}); // FIX: Changed from {} to useState({})

  // Calculation results
  const [regularMealCostPerMeal, setRegularMealCostPerMeal] = useState(0);
  const [showSummaryTable, setShowSummaryTable] = useState(false);

  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');

  // Refs for focusing inputs
  const firstInputRef = useRef(null);

  // --- Utility Functions ---

  // Show custom dialog message
  const showCustomDialog = (message) => {
    setDialogMessage(message);
    setShowDialog(true);
  };

  // Close custom dialog
  const closeCustomDialog = () => {
    setShowDialog(false);
    setDialogMessage('');
  };

  // Focus on the first input when step changes
  useEffect(() => {
    if (firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [currentStep]);

  // Handle changes for input fields
  const handleInputChange = (e, setStateFunction) => {
    const value = e.target.value;
    if (e.target.type === 'number' && value !== '') {
      setStateFunction(Number(value));
    } else {
      setStateFunction(value);
    }
  };

  // --- Step Navigation Functions ---

  const nextStep = () => {
    switch (currentStep) {
      case 1:
        if (numMembers > 0) {
          const initialMembers = Array.from({ length: numMembers }, (_, i) => ({
            id: `member-${i + 1}`,
            name: '',
            totalAdvanced: 0,
            totalMeals: 0,
            isGuestMealMember: false,
            guestMealCost: 0,
            mealCost: 0, // Regular meal cost for this member
            individualShare: {
              // Share of common expenses
              maasi: 0,
              gas: 0,
              others: 0,
              wifi: 0,
              electric: 0,
            },
            totalCost: 0, // Sum of all costs for this member
            dueAmount: 0, // totalAdvanced - totalCost
          }));
          setMembers(initialMembers);
          setCurrentStep(2);
        } else {
          showCustomDialog(
            'Please enter a valid number of members (greater than 0).'
          );
        }
        break;
      case 2:
        if (members.every((member) => member.name.trim() !== '')) {
          setUnassignedMembers(members.map((m) => m.id)); // All members are initially unassigned
          setSelectedMemberForDetails(members[0]?.id || ''); // Select first member by default
          setCurrentStep(3);
        } else {
          showCustomDialog('Please enter a name for all members.');
        }
        break;
      case 3:
        if (unassignedMembers.length === 0) {
          setCurrentStep(4);
        } else {
          showCustomDialog(
            'Please enter Total Advanced and Total Meals for all members before proceeding.'
          );
        }
        break;
      case 4:
        const selectedGuestCount = Object.values(selectedGuestMembers).filter(
          (val) => (typeof val === 'object' ? val.isSelected : false)
        ).length;
        if (numGuestMealMembers !== '' && numGuestMealMembers < 0) {
          showCustomDialog('Number of guest meal members cannot be negative.');
          return;
        }

        if (
          numGuestMealMembers > 0 &&
          selectedGuestCount !== numGuestMealMembers
        ) {
          showCustomDialog(
            `Please select exactly ${numGuestMealMembers} members for guest meals.`
          );
          return;
        }
        if (numGuestMealMembers > 0) {
          const allGuestCostsEntered = Object.values(
            selectedGuestMembers
          ).every((member) => {
            if (member.isSelected) {
              return member.cost !== '' && member.cost >= 0;
            }
            return true;
          });

          if (!allGuestCostsEntered) {
            showCustomDialog(
              'Please enter a valid guest meal cost (non-negative) for all selected guest members.'
            );
            return;
          }
        }
        setCurrentStep(5);
        break;
      case 5:
        if (totalBazarCost !== '' && totalBazarCost >= 0) {
          setCurrentStep(6);
        } else {
          showCustomDialog(
            'Please enter a valid Total Bazar Cost (non-negative).'
          );
        }
        break;
      case 6:
        // Basic validation for costs
        if (maasiCost !== '' && maasiCost < 0) {
          showCustomDialog('Maasi Cost cannot be negative.');
          return;
        }
        if (gasCost !== '' && gasCost < 0) {
          showCustomDialog('Gas Cost cannot be negative.');
          return;
        }
        if (othersCost !== '' && othersCost < 0) {
          showCustomDialog('Others Cost cannot be negative.');
          return;
        }
        if (wifiBill !== '' && wifiBill < 0) {
          showCustomDialog('WiFi Bill cannot be negative.');
          return;
        }
        if (electricBill !== '' && electricBill < 0) {
          showCustomDialog('Electric Bill cannot be negative.');
          return;
        }

        // Validation for 'select' distribution if a cost is entered
        if (
          maasiDistribution === 'select' &&
          maasiCost > 0 &&
          Object.values(selectedMaasiMembers).filter((v) => v).length === 0
        ) {
          showCustomDialog(
            'Please select at least one member for Maasi Cost distribution.'
          );
          return;
        }
        if (
          gasDistribution === 'select' &&
          gasCost > 0 &&
          Object.values(selectedGasMembers).filter((v) => v).length === 0
        ) {
          showCustomDialog(
            'Please select at least one member for Gas Cost distribution.'
          );
          return;
        }
        if (
          othersDistribution === 'select' &&
          othersCost > 0 &&
          Object.values(selectedOthersMembers).filter((v) => v).length === 0
        ) {
          showCustomDialog(
            'Please select at least one member for Others Cost distribution.'
          );
          return;
        }
        if (
          wifiDistribution === 'select' &&
          wifiBill > 0 &&
          Object.values(selectedWifiMembers).filter((v) => v).length === 0
        ) {
          showCustomDialog(
            'Please select at least one member for WiFi Bill distribution.'
          );
          return;
        }
        if (
          electricDistribution === 'select' &&
          electricBill > 0 &&
          Object.values(selectedElectricMembers).filter((v) => v).length === 0
        ) {
          showCustomDialog(
            'Please select at least one member for Electric Bill distribution.'
          );
          return;
        }

        calculateAndDisplayResults();
        break;
      default:
        break;
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      if (currentStep === 3) {
        // When going back from step 3 (member details), re-enable all members for selection
        setUnassignedMembers(members.map((m) => m.id));
        setSelectedMemberForDetails(members[0]?.id || '');
      } else if (currentStep === 4) {
        // When going back from step 4 (guest meals), reset guest meal selection
        setSelectedGuestMembers({});
        setNumGuestMealMembers('');
        setGuestMealSelectionCount(0);
      } else if (currentStep === 7) {
        setShowSummaryTable(false);
      }
      setCurrentStep(currentStep - 1);
    }
  };

  // --- Step 3 Specific Handlers ---

  const handleMemberSelectForDetails = (e) => {
    setSelectedMemberForDetails(e.target.value);
    const selectedMember = members.find((m) => m.id === e.target.value);
    if (selectedMember) {
      setCurrentAdvanced(
        selectedMember.totalAdvanced === 0 ? '' : selectedMember.totalAdvanced
      );
      setCurrentMeals(
        selectedMember.totalMeals === 0 ? '' : selectedMember.totalMeals
      );
    }
  };

  const saveMemberDetailsAndContinue = () => {
    if (
      selectedMemberForDetails &&
      currentAdvanced !== '' &&
      currentMeals !== '' &&
      currentAdvanced >= 0 &&
      currentMeals >= 0
    ) {
      const updatedMembers = members.map((member) =>
        member.id === selectedMemberForDetails
          ? {
              ...member,
              totalAdvanced: Number(currentAdvanced),
              totalMeals: Number(currentMeals),
            }
          : member
      );
      setMembers(updatedMembers);
      setUnassignedMembers((prev) =>
        prev.filter((id) => id !== selectedMemberForDetails)
      );

      // Clear inputs for next member
      setCurrentAdvanced('');
      setCurrentMeals('');

      // Select the next unassigned member, if any
      const nextUnassigned = unassignedMembers.find(
        (id) => id !== selectedMemberForDetails
      );
      setSelectedMemberForDetails(nextUnassigned || '');

      if (!nextUnassigned) {
        showCustomDialog(
          'All member details have been saved. Click "Next" to proceed to the next step.'
        );
      }
    } else {
      showCustomDialog(
        'Please select a member and enter valid (non-negative) Total Advanced Rupees and Total Meals.'
      );
    }
  };

  const editMemberDetails = (memberId) => {
    const memberToEdit = members.find((m) => m.id === memberId);
    if (memberToEdit) {
      setSelectedMemberForDetails(memberId);
      setCurrentAdvanced(memberToEdit.totalAdvanced);
      setCurrentMeals(memberToEdit.totalMeals);
      // Add the member back to unassignedMembers so they can be selected and resaved
      setUnassignedMembers((prev) => {
        if (!prev.includes(memberId)) {
          return [...prev, memberId];
        }
        return prev;
      });
    }
    // Set focus on the first input field
    if (firstInputRef.current) {
      firstInputRef.current.focus();
    }
  };

  // --- Step 4 Specific Handlers ---

  const handleGuestMealCountChange = (e) => {
    const value = Number(e.target.value);
    setNumGuestMealMembers(value);
    // Reset selections if count changes
    setSelectedGuestMembers({});
    setGuestMealSelectionCount(0);
  };

  const handleGuestMemberSelection = (memberId, isSelected) => {
    setSelectedGuestMembers((prev) => {
      const newSelections = { ...prev };
      if (isSelected) {
        if (guestMealSelectionCount < numGuestMealMembers) {
          newSelections[memberId] = { isSelected: true, cost: '' };
          setGuestMealSelectionCount((prevCount) => prevCount + 1);
        } else {
          showCustomDialog(
            `You can only select ${numGuestMealMembers} members for guest meals.`
          );
          return prev; // Do not update if limit reached
        }
      } else {
        delete newSelections[memberId];
        setGuestMealSelectionCount((prevCount) => prevCount - 1);
      }
      return newSelections;
    });
  };

  const handleGuestMealCostChange = (memberId, cost) => {
    setSelectedGuestMembers((prev) => ({
      ...prev,
      [memberId]: { ...prev[memberId], cost: Number(cost) },
    }));
  };

  // --- Step 6 Specific Handlers (Common Expenses) ---

  const handleDistributionChange = (costType, value) => {
    switch (costType) {
      case 'maasi':
        setMaasiDistribution(value);
        setSelectedMaasiMembers({});
        break;
      case 'gas':
        setGasDistribution(value);
        setSelectedGasMembers({});
        break;
      case 'others':
        setOthersDistribution(value);
        setSelectedOthersMembers({});
        break;
      case 'wifibill':
        setWifiDistribution(value);
        setSelectedWifiMembers({});
        break; // Corrected case for WiFi Bill
      case 'electricbill':
        setElectricDistribution(value);
        setSelectedElectricMembers({});
        break; // Corrected case for Electric Bill
      default:
        break;
    }
  };

  const handleSelectedMemberForCost = (costType, memberId, isChecked) => {
    const updateSelectedMembers = (prevSelected) => {
      const newSelected = { ...prevSelected };
      if (isChecked) {
        newSelected[memberId] = true;
      } else {
        delete newSelected[memberId];
      }
      return newSelected;
    };

    switch (costType) {
      case 'maasi':
        setSelectedMaasiMembers(updateSelectedMembers);
        break;
      case 'gas':
        setSelectedGasMembers(updateSelectedMembers);
        break;
      case 'others':
        setSelectedOthersMembers(updateSelectedMembers);
        break;
      case 'wifibill':
        setSelectedWifiMembers(updateSelectedMembers);
        break; // Corrected case for WiFi Bill
      case 'electricbill':
        setSelectedElectricMembers(updateSelectedMembers);
        break; // Corrected case for Electric Bill
      default:
        break;
    }
  };

  // --- Main Calculation Logic (Step 7 & 8) ---

  const calculateAndDisplayResults = () => {
    let updatedMembers = [...members];

    // 1. Calculate TOTAL MEALs and TOTAL ADVANCED (overall)
    const totalMealsOverall = updatedMembers.reduce(
      (sum, member) => sum + member.totalMeals,
      0
    );
    const totalAdvancedOverall = updatedMembers.reduce(
      (sum, member) => sum + member.totalAdvanced,
      0
    );

    // 2. Calculate TOTAL GUEST MEAL COSTs on that month
    let totalGuestMealCostsOverall = 0;
    updatedMembers = updatedMembers.map((member) => {
      // Ensure guest meal info is handled for existing members if table was edited
      const guestInfo = selectedGuestMembers[member.id];
      if (member.isGuestMealMember && member.guestMealCost > 0) {
        // If it was already a guest member from previous calc
        totalGuestMealCostsOverall += member.guestMealCost;
      } else if (guestInfo && guestInfo.isSelected) {
        // If it's a new selection from Step 4
        member.guestMealCost = Number(guestInfo.cost) || 0;
        totalGuestMealCostsOverall += member.guestMealCost;
      } else {
        member.isGuestMealMember = false;
        member.guestMealCost = 0;
      }
      return member;
    });

    // 3. Evaluate REGULAR MEAL COST per MEAL
    let calculatedRegularMealCostPerMeal = 0;
    const netBazarCost = totalBazarCost - totalGuestMealCostsOverall;

    if (totalMealsOverall > 0) {
      calculatedRegularMealCostPerMeal = netBazarCost / totalMealsOverall;
    }
    setRegularMealCostPerMeal(calculatedRegularMealCostPerMeal);

    // 4. Calculate TOTAL MEAL COST for each member
    updatedMembers = updatedMembers.map((member) => ({
      ...member,
      mealCost: calculatedRegularMealCostPerMeal * member.totalMeals,
    }));

    // 5. Distribute common costs
    const distributeCost = (cost, distributionType, selectedMemberMap) => {
      // Ensure cost is a valid number, default to 0 if not
      const actualCost = Number(cost) || 0;
      if (actualCost <= 0) return {};

      let shares = {};
      if (distributionType === 'same') {
        updatedMembers.forEach((member) => {
          shares[member.id] = actualCost;
        });
      } else if (distributionType === 'divided') {
        const activeMembersCount = updatedMembers.length;
        if (activeMembersCount > 0) {
          const sharePerMember = actualCost / activeMembersCount;
          updatedMembers.forEach((member) => {
            shares[member.id] = sharePerMember;
          });
        }
      } else if (distributionType === 'select') {
        const selectedIds = Object.keys(selectedMemberMap).filter(
          (id) => selectedMemberMap[id]
        );
        if (selectedIds.length > 0) {
          const sharePerSelected = actualCost / selectedIds.length;
          selectedIds.forEach((id) => {
            shares[id] = sharePerSelected;
          });
        }
      }
      return shares;
    };

    const maasiShares = distributeCost(
      maasiCost,
      maasiDistribution,
      selectedMaasiMembers
    );
    const gasShares = distributeCost(
      gasCost,
      gasDistribution,
      selectedGasMembers
    );
    const othersShares = distributeCost(
      othersCost,
      othersDistribution,
      selectedOthersMembers
    );
    const wifiShares = distributeCost(
      wifiBill,
      wifiDistribution,
      selectedWifiMembers
    );
    const electricShares = distributeCost(
      electricBill,
      electricDistribution,
      selectedElectricMembers
    );

    // 6. COUNT total costs of each member
    updatedMembers = updatedMembers.map((member) => {
      const individualShare = {
        maasi: maasiShares[member.id] || 0,
        gas: gasShares[member.id] || 0,
        others: othersShares[member.id] || 0,
        wifi: wifiShares[member.id] || 0,
        electric: electricShares[member.id] || 0,
      };

      const totalMemberCost =
        member.mealCost +
        member.guestMealCost +
        individualShare.maasi +
        individualShare.gas +
        individualShare.others +
        individualShare.wifi +
        individualShare.electric;

      const dueAmount = member.totalAdvanced - totalMemberCost;

      return {
        ...member,
        individualShare,
        totalCost: totalMemberCost,
        dueAmount: dueAmount,
      };
    });

    setMembers(updatedMembers);
    setShowSummaryTable(true);
    setCurrentStep(7); // Move to the summary display step
  };

  // --- Handlers for in-table editing (Step 7) ---
  const handleTableEdit = (memberId, field, value) => {
    const updatedMembers = members.map((member) => {
      if (member.id === memberId) {
        const numericValue = Number(value);
        if (field === 'totalMeals') {
          return { ...member, totalMeals: numericValue };
        } else if (field === 'totalAdvanced') {
          return { ...member, totalAdvanced: numericValue };
        } else if (field === 'guestMealCost') {
          // Update selectedGuestMembers if guestMealCost is changed directly in table
          setSelectedGuestMembers((prev) => ({
            ...prev,
            [memberId]: {
              isSelected: true, // Assume selected if cost is being edited
              cost: numericValue,
            },
          }));
          return {
            ...member,
            guestMealCost: numericValue,
            isGuestMealMember: true,
          };
        }
      }
      return member;
    });
    setMembers(updatedMembers);
    // Recalculate everything after an edit
    // Note: This re-runs all calculations from Step 6 based on updated 'members' state
    // and current totalBazarCost, maasiCost etc. This is important for consistency.
    setTimeout(calculateAndDisplayResults, 0); // Use setTimeout to ensure state update propagates
  };

  // --- Export Functions ---
  const downloadPdf = () => {
    // Check if jsPDF is available globally
    if (typeof window.jsPDF === 'undefined') {
      showCustomDialog(
        'PDF generation library not loaded. Please try refreshing the page or check your internet connection.'
      );
      return;
    }
    const doc = new (window.jsPDF || window.jspdf?.jsPDF)();
 // Access jsPDF from window
    doc.setFontSize(18);
    doc.text('Meal & Expense Summary', 14, 22);

    doc.setFontSize(12);
    doc.text(
      `Regular Meal Cost per Meal: ₹${regularMealCostPerMeal.toFixed(2)}`,
      14,
      30
    );
    doc.text(`Total Bazar Cost: ₹${Number(totalBazarCost).toFixed(2)}`, 14, 37);
    doc.text(
      `Total Guest Meal Costs: ₹${members
        .reduce((sum, m) => sum + m.guestMealCost, 0)
        .toFixed(2)}`,
      14,
      44
    );
    doc.text(
      `Total Meals (Overall): ${members.reduce(
        (sum, member) => sum + member.totalMeals,
        0
      )}`,
      14,
      51
    );
    doc.text(
      `Total Advanced (Overall): ₹${members
        .reduce((sum, member) => sum + member.totalAdvanced, 0)
        .toFixed(2)}`,
      14,
      58
    );

    const head = [
      [
        'SL NO',
        'NAME',
        'MEAL NO',
        'MEAL AMOUNT (₹)',
        'GAS (₹)',
        'GUEST (₹)',
        'OTHERS (₹)',
        'WIFI (₹)',
        'ELECTRIC (₹)',
        'MAASI (₹)',
        'TOTAL ADVANCE (₹)',
        'DUE / RETURN (₹)',
      ],
    ];
    const body = members.map((member, index) => [
      index + 1,
      member.name,
      member.totalMeals,
      member.mealCost.toFixed(2),
      member.individualShare.gas.toFixed(2),
      member.guestMealCost.toFixed(2),
      member.individualShare.others.toFixed(2),
      member.individualShare.wifi.toFixed(2),
      member.individualShare.electric.toFixed(2),
      member.individualShare.maasi.toFixed(2),
      member.totalAdvanced.toFixed(2),
      member.dueAmount.toFixed(2),
    ]);

    const totalRow = [
      '',
      'TOTAL',
      members.reduce((sum, m) => sum + m.totalMeals, 0),
      members.reduce((sum, m) => sum + m.mealCost, 0).toFixed(2),
      members.reduce((sum, m) => sum + m.individualShare.gas, 0).toFixed(2),
      members.reduce((sum, m) => sum + m.guestMealCost, 0).toFixed(2),
      members.reduce((sum, m) => sum + m.individualShare.others, 0).toFixed(2),
      members.reduce((sum, m) => sum + m.individualShare.wifi, 0).toFixed(2),
      members
        .reduce((sum, m) => sum + m.individualShare.electric, 0)
        .toFixed(2),
      members.reduce((sum, m) => sum + m.individualShare.maasi, 0).toFixed(2),
      members.reduce((sum, m) => sum + m.totalAdvanced, 0).toFixed(2),
      members.reduce((sum, m) => sum + m.dueAmount, 0).toFixed(2),
    ];
    body.push(totalRow);

    doc.autoTable({
      head: head,
      body: body,
      startY: 70, // Start table after the overall totals
      theme: 'grid',
      styles: { cellPadding: 2, fontSize: 8, overflow: 'linebreak' },
      headStyles: {
        fillColor: [200, 200, 200],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
      },
      columnStyles: {
        // Adjust column widths based on content
        0: { cellWidth: 15 }, // SL NO
        1: { cellWidth: 25 }, // NAME
        2: { cellWidth: 20 }, // MEAL NO
        3: { cellWidth: 25 }, // MEAL AMOUNT
        4: { cellWidth: 20 }, // GAS
        5: { cellWidth: 20 }, // GUEST
        6: { cellWidth: 20 }, // OTHERS
        7: { cellWidth: 20 }, // WIFI
        8: { cellWidth: 20 }, // ELECTRIC
        9: { cellWidth: 20 }, // MAASI
        10: { cellWidth: 25 }, // TOTAL ADVANCE
        11: { cellWidth: 25 }, // DUE / RETURN
      },
      didParseCell: function (data) {
        // Apply row background color for due/return
        if (data.section === 'body' && data.row.index < members.length) {
          const member = members[data.row.index];
          if (member.dueAmount < 0) {
            data.cell.styles.fillColor = [255, 235, 238]; // Light Red
          } else {
            data.cell.styles.fillColor = [232, 245, 233]; // Light Green
          }
        }
        // Style total row
        if (data.section === 'body' && data.row.index === members.length) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [240, 240, 240]; // Light grey for total row
        }
      },
    });

    doc.save('meal_expense_summary.pdf');
  };

  const downloadExcel = () => {
    // Check if XLSX is available globally
    if (typeof window.XLSX === 'undefined') {
      showCustomDialog(
        'Excel generation library not loaded. Please try refreshing the page or check your internet connection.'
      );
      return;
    }

    const data = members.map((member, index) => ({
      'SL NO': index + 1,
      NAME: member.name,
      'MEAL NO': member.totalMeals,
      'MEAL AMOUNT (₹)': member.mealCost.toFixed(2),
      'GAS (₹)': member.individualShare.gas.toFixed(2),
      'GUEST (₹)': member.guestMealCost.toFixed(2),
      'OTHERS (₹)': member.individualShare.others.toFixed(2),
      'WIFI (₹)': member.individualShare.wifi.toFixed(2),
      'ELECTRIC (₹)': member.individualShare.electric.toFixed(2),
      'MAASI (₹)': member.individualShare.maasi.toFixed(2),
      'TOTAL ADVANCE (₹)': member.totalAdvanced.toFixed(2),
      'DUE / RETURN (₹)': member.dueAmount.toFixed(2),
    }));

    // Add total row
    data.push({
      'SL NO': '',
      NAME: 'TOTAL',
      'MEAL NO': members.reduce((sum, m) => sum + m.totalMeals, 0),
      'MEAL AMOUNT (₹)': members
        .reduce((sum, m) => sum + m.mealCost, 0)
        .toFixed(2),
      'GAS (₹)': members
        .reduce((sum, m) => sum + m.individualShare.gas, 0)
        .toFixed(2),
      'GUEST (₹)': members
        .reduce((sum, m) => sum + m.guestMealCost, 0)
        .toFixed(2),
      'OTHERS (₹)': members
        .reduce((sum, m) => sum + m.individualShare.others, 0)
        .toFixed(2),
      'WIFI (₹)': members
        .reduce((sum, m) => sum + m.individualShare.wifi, 0)
        .toFixed(2),
      'ELECTRIC (₹)': members
        .reduce((sum, m) => sum + m.individualShare.electric, 0)
        .toFixed(2),
      'MAASI (₹)': members
        .reduce((sum, m) => sum + m.individualShare.maasi, 0)
        .toFixed(2),
      'TOTAL ADVANCE (₹)': members
        .reduce((sum, m) => sum + m.totalAdvanced, 0)
        .toFixed(2),
      'DUE / RETURN (₹)': members
        .reduce((sum, m) => sum + m.dueAmount, 0)
        .toFixed(2),
    });

    const ws = window.XLSX.utils.json_to_sheet(data); // Access XLSX from window
    const wb = window.XLSX.utils.book_new(); // Access XLSX from window
    window.XLSX.utils.book_append_sheet(wb, ws, 'Meal Summary'); // Access XLSX from window
    window.XLSX.writeFile(wb, 'meal_expense_summary.xlsx'); // Access XLSX from window
  };

  // --- JSX for each step ---

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h2 className="text-3xl font-semibold text-gray-800 text-center">
              Step 1: Number of Members
            </h2>
            <div className="flex flex-col items-center">
              <label
                htmlFor="numMembers"
                className="text-xl font-medium text-gray-700 mb-2"
              >
                Total Number of Members:
              </label>
              <input
                ref={firstInputRef}
                type="number"
                id="numMembers"
                value={numMembers}
                onChange={(e) => handleInputChange(e, setNumMembers)}
                min="1"
                className="w-full md:w-1/2 p-3 border-2 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                placeholder="e.g., 3"
              />
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-6">
            <h2 className="text-3xl font-semibold text-gray-800 text-center">
              Step 2: Enter Member Names
            </h2>
            {members.map((member, index) => (
              <div key={member.id} className="flex flex-col items-center">
                <label
                  htmlFor={`member-name-${index}`}
                  className="text-xl font-medium text-gray-700 mb-2"
                >
                  Member {index + 1} Name:
                </label>
                <input
                  ref={index === 0 ? firstInputRef : null} // Only first input
                  type="text"
                  id={`member-name-${index}`}
                  value={member.name}
                  onChange={(e) => {
                    const newMembers = [...members];
                    newMembers[index].name = e.target.value;
                    setMembers(newMembers);
                  }}
                  className="w-full md:w-1/2 p-3 border-2 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg"
                  placeholder="e.g., John Doe"
                />
              </div>
            ))}
          </div>
        );
      case 3:
        const currentMemberName =
          members.find((m) => m.id === selectedMemberForDetails)?.name ||
          'Select Member';
        const enteredMembers = members.filter(
          (member) => !unassignedMembers.includes(member.id)
        );

        return (
          <div className="space-y-6">
            <h2 className="text-3xl font-semibold text-gray-800 text-center">
              Step 3: Member Details
            </h2>
            <div className="flex flex-col items-center space-y-4">
              {unassignedMembers.length > 0 && (
                <>
                  <label
                    htmlFor="select-member-details"
                    className="text-xl font-medium text-gray-700"
                  >
                    Select Member:
                  </label>
                  <select
                    id="select-member-details"
                    value={selectedMemberForDetails}
                    onChange={handleMemberSelectForDetails}
                    className="w-full md:w-1/2 p-3 border-2 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg"
                  >
                    <option value="" disabled>
                      Choose a member
                    </option>
                    {unassignedMembers.map((memberId) => {
                      const member = members.find((m) => m.id === memberId);
                      return (
                        <option key={memberId} value={memberId}>
                          {member.name}
                        </option>
                      );
                    })}
                  </select>
                </>
              )}

              {selectedMemberForDetails && (
                <div className="w-full md:w-1/2 p-6 bg-white rounded-lg shadow-md space-y-4">
                  <h3 className="text-2xl font-semibold text-center text-gray-700">
                    Details for {currentMemberName}
                  </h3>
                  <div>
                    <label
                      htmlFor="total-advanced"
                      className="block text-lg font-medium text-gray-700 mb-1"
                    >
                      Total Advanced (₹):
                    </label>
                    <input
                      ref={firstInputRef}
                      type="number"
                      id="total-advanced"
                      value={currentAdvanced}
                      onChange={(e) => handleInputChange(e, setCurrentAdvanced)}
                      min="0"
                      className="w-full p-3 border-2 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      placeholder="e.g., 5000"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="total-meals"
                      className="block text-lg font-medium text-gray-700 mb-1"
                    >
                      Total Meals for the month:
                    </label>
                    <input
                      type="number"
                      id="total-meals"
                      value={currentMeals}
                      onChange={(e) => handleInputChange(e, setCurrentMeals)}
                      min="0"
                      className="w-full p-3 border-2 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      placeholder="e.g., 30"
                    />
                  </div>
                  <button
                    onClick={saveMemberDetailsAndContinue}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg transition duration-300 ease-in-out text-lg transform hover:scale-105"
                  >
                    Save & Continue
                  </button>
                </div>
              )}

              {unassignedMembers.length === 0 && (
                <p className="text-xl text-green-700 font-semibold mt-4">
                  All members details entered! Click "Next" to continue.
                </p>
              )}

              {/* Display saved members details */}
              {enteredMembers.length > 0 && (
                <div className="w-full md:w-1/2 p-6 bg-gray-50 rounded-lg shadow-inner mt-6">
                  <h3 className="text-2xl font-semibold text-gray-700 text-center mb-4">
                    Saved Member Details
                  </h3>
                  <ul className="space-y-2">
                    {enteredMembers.map((member) => (
                      <li
                        key={member.id}
                        className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex justify-between items-center"
                      >
                        <div>
                          <p className="font-bold text-lg text-indigo-700">
                            {member.name}
                          </p>
                          <p className="text-gray-600">
                            Total Advanced: ₹{member.totalAdvanced.toFixed(2)}
                          </p>
                          <p className="text-gray-600">
                            Total Meals: {member.totalMeals}
                          </p>
                        </div>
                        <button
                          onClick={() => editMemberDetails(member.id)}
                          className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out text-sm transform hover:scale-105"
                        >
                          Edit
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        );
      case 4:
        const selectedGuestCountDisplay = Object.values(
          selectedGuestMembers
        ).filter((g) => g.isSelected).length;
        return (
          <div className="space-y-6">
            <h2 className="text-3xl font-semibold text-gray-800 text-center">
              Step 4: Guest Meal Details
            </h2>
            <div className="flex flex-col items-center space-y-4">
              <label
                htmlFor="num-guest-members"
                className="text-xl font-medium text-gray-700 mb-2"
              >
                Number of members having GUEST MEAL:
              </label>
              <input
                ref={firstInputRef}
                type="number"
                id="num-guest-members"
                value={numGuestMealMembers}
                onChange={handleGuestMealCountChange}
                min="0"
                className="w-full md:w-1/2 p-3 border-2 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                placeholder="e.g., 2"
              />

              {numGuestMealMembers > 0 && (
                <div className="w-full md:w-3/4 p-6 bg-white rounded-lg shadow-md space-y-4">
                  <h3 className="text-2xl font-semibold text-gray-700 text-center">
                    Select Guest Meal Members ({selectedGuestCountDisplay} /{' '}
                    {numGuestMealMembers})
                  </h3>
                  {members.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center justify-between py-2 border-b border-gray-200 last:border-b-0"
                    >
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id={`guest-member-${member.id}`}
                          checked={
                            selectedGuestMembers[member.id]?.isSelected || false
                          }
                          onChange={(e) =>
                            handleGuestMemberSelection(
                              member.id,
                              e.target.checked
                            )
                          }
                          className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          // Disable checkbox if max selected members reached and this member is not already selected
                          disabled={
                            !selectedGuestMembers[member.id]?.isSelected &&
                            guestMealSelectionCount >= numGuestMealMembers
                          }
                        />
                        <label
                          htmlFor={`guest-member-${member.id}`}
                          className="ml-3 text-lg font-medium text-gray-700"
                        >
                          {member.name}
                        </label>
                      </div>
                      {selectedGuestMembers[member.id]?.isSelected && (
                        <div className="flex items-center space-x-2">
                          <label
                            htmlFor={`guest-cost-${member.id}`}
                            className="text-lg text-gray-600"
                          >
                            Cost (₹):
                          </label>
                          <input
                            type="number"
                            id={`guest-cost-${member.id}`}
                            value={selectedGuestMembers[member.id]?.cost || ''}
                            onChange={(e) =>
                              handleGuestMealCostChange(
                                member.id,
                                e.target.value
                              )
                            }
                            min="0"
                            className="w-32 p-2 border-2 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            placeholder="e.g., 150"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      case 5:
        return (
          <div className="space-y-6">
            <h2 className="text-3xl font-semibold text-gray-800 text-center">
              Step 5: Total Bazar Cost
            </h2>
            <div className="flex flex-col items-center">
              <label
                htmlFor="total-bazar-cost"
                className="text-xl font-medium text-gray-700 mb-2"
              >
                TOTAL BAZAR COST (₹) for the month:
              </label>
              <input
                ref={firstInputRef}
                type="number"
                id="total-bazar-cost"
                value={totalBazarCost}
                onChange={(e) => handleInputChange(e, setTotalBazarCost)}
                min="0"
                className="w-full md:w-1/2 p-3 border-2 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                placeholder="e.g., 15000"
              />
            </div>
          </div>
        );
      case 6:
        const renderCostInput = (
          costState,
          setCostState,
          distributionState,
          setDistributionState,
          selectedMembersState,
          setSelectedMembersState,
          costName
        ) => (
          <div className="w-full p-6 bg-white rounded-lg shadow-md space-y-4">
            <h3 className="text-2xl font-semibold text-gray-700 text-center">
              {costName} Cost (₹)
            </h3>
            <div>
              <label
                htmlFor={`${costName.toLowerCase().replace(/\s/g, '-')}-cost`}
                className="block text-lg font-medium text-gray-700 mb-1"
              >
                Amount:
              </label>
              <input
                type="number"
                id={`${costName.toLowerCase().replace(/\s/g, '-')}-cost`}
                value={costState}
                onChange={(e) => handleInputChange(e, setCostState)}
                min="0"
                className="w-full p-3 border-2 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                placeholder={`e.g., ${costName === 'Maasi' ? '3000' : '500'}`}
              />
            </div>
            <div>
              <label
                htmlFor={`${costName
                  .toLowerCase()
                  .replace(/\s/g, '-')}-distribution`}
                className="block text-lg font-medium text-gray-700 mb-1"
              >
                Distribution:
              </label>
              <select
                id={`${costName
                  .toLowerCase()
                  .replace(/\s/g, '-')}-distribution`}
                value={distributionState}
                onChange={(e) =>
                  handleDistributionChange(
                    costName.replace(/\s/g, '').toLowerCase(),
                    e.target.value
                  )
                }
                className="w-full p-3 border-2 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg"
              >
                <option value="divided">Divided among everyone</option>
                <option value="same">Same for everyone</option>
                <option value="select">Select members</option>
              </select>
            </div>
            {distributionState === 'select' &&
              (costState || costState === 0) && (
                <div className="mt-4 border-t pt-4 border-gray-200">
                  <p className="text-lg font-medium text-gray-700 mb-2">
                    Select members for {costName}:
                  </p>
                  {members.map((member) => (
                    <div
                      key={`${costName}-${member.id}`}
                      className="flex items-center mb-2"
                    >
                      <input
                        type="checkbox"
                        id={`${costName}-${member.id}-checkbox`}
                        checked={selectedMembersState[member.id] || false}
                        onChange={(e) =>
                          handleSelectedMemberForCost(
                            costName.replace(/\s/g, '').toLowerCase(),
                            member.id,
                            e.target.checked
                          )
                        }
                        className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor={`${costName}-${member.id}-checkbox`}
                        className="ml-3 text-md text-gray-700"
                      >
                        {member.name}
                      </label>
                    </div>
                  ))}
                </div>
              )}
          </div>
        );

        return (
          <div className="space-y-6">
            <h2 className="text-3xl font-semibold text-gray-800 text-center">
              Step 6: Other Monthly Costs
            </h2>
            <div className="flex flex-col items-center space-y-6 w-full max-w-2xl mx-auto">
              {renderCostInput(
                maasiCost,
                setMaasiCost,
                maasiDistribution,
                setMaasiDistribution,
                selectedMaasiMembers,
                setSelectedMaasiMembers,
                'Maasi'
              )}
              {renderCostInput(
                gasCost,
                setGasCost,
                gasDistribution,
                setGasDistribution,
                selectedGasMembers,
                setSelectedGasMembers,
                'Gas'
              )}
              {renderCostInput(
                othersCost,
                setOthersCost,
                othersDistribution,
                setOthersDistribution,
                selectedOthersMembers,
                setSelectedOthersMembers,
                'Others'
              )}
              {renderCostInput(
                wifiBill,
                setWifiBill,
                wifiDistribution,
                setWifiDistribution,
                selectedWifiMembers,
                setSelectedWifiMembers,
                'WiFi Bill'
              )}
              {renderCostInput(
                electricBill,
                setElectricBill,
                electricDistribution,
                setElectricDistribution,
                selectedElectricMembers,
                setSelectedElectricMembers,
                'Electric Bill'
              )}
            </div>
          </div>
        );
      case 7:
        const totalMealsCalculated = members.reduce(
          (sum, member) => sum + member.totalMeals,
          0
        );
        const totalAdvancedCalculated = members.reduce(
          (sum, member) => sum + member.totalAdvanced,
          0
        );

        return (
          <div className="space-y-6">
            <h2 className="text-3xl font-semibold text-gray-800 text-center mb-6">
              Step 7: Calculation Summary
            </h2>
            <div className="bg-white p-6 rounded-lg shadow-md mb-6 max-w-xl mx-auto">
              <h3 className="text-2xl font-semibold text-gray-800 mb-4 text-center">
                Overall Totals
              </h3>
              <p className="text-lg text-gray-700 mb-2">
                <span className="font-semibold">Total Meals (Overall):</span>{' '}
                {totalMealsCalculated}
              </p>
              <p className="text-lg text-gray-700 mb-2">
                <span className="font-semibold">Total Advanced (Overall):</span>{' '}
                ₹{totalAdvancedCalculated.toFixed(2)}
              </p>
              <p className="text-lg text-gray-700 mb-2">
                <span className="font-semibold">Total Bazar Cost:</span> ₹
                {Number(totalBazarCost).toFixed(2)}
              </p>
              <p className="text-lg text-gray-700 mb-2">
                <span className="font-semibold">Total Guest Meal Costs:</span> ₹
                {members
                  .reduce((sum, m) => sum + m.guestMealCost, 0)
                  .toFixed(2)}
              </p>
              <p className="text-lg text-gray-700 font-bold">
                <span className="font-semibold">
                  Regular Meal Cost per Meal:
                </span>{' '}
                ₹{regularMealCostPerMeal.toFixed(2)}
              </p>
            </div>

            {showSummaryTable && (
              <div className="overflow-x-auto bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-2xl font-semibold text-gray-800 mb-4 text-center">
                  Member-wise Details
                </h3>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider rounded-tl-lg">
                        SL NO
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        NAME
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        MEAL NO
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        MEAL AMOUNT (₹)
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        GAS (₹)
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        GUEST (₹)
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        OTHERS (₹)
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        WIFI (₹)
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ELECTRIC (₹)
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        MAASI (₹)
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        TOTAL ADVANCE (₹)
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider rounded-tr-lg">
                        DUE / RETURN (₹)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {members.map((member, index) => (
                      <tr
                        key={member.id}
                        className={
                          member.dueAmount < 0 ? 'bg-red-100' : 'bg-green-100'
                        }
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {index + 1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {member.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <input
                            type="number"
                            value={member.totalMeals}
                            onChange={(e) =>
                              handleTableEdit(
                                member.id,
                                'totalMeals',
                                e.target.value
                              )
                            }
                            className="w-20 p-1 border rounded text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {member.mealCost.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {member.individualShare.gas.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <input
                            type="number"
                            value={member.guestMealCost}
                            onChange={(e) =>
                              handleTableEdit(
                                member.id,
                                'guestMealCost',
                                e.target.value
                              )
                            }
                            className="w-20 p-1 border rounded text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {member.individualShare.others.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {member.individualShare.wifi.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {member.individualShare.electric.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {member.individualShare.maasi.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <input
                            type="number"
                            value={member.totalAdvanced}
                            onChange={(e) =>
                              handleTableEdit(
                                member.id,
                                'totalAdvanced',
                                e.target.value
                              )
                            }
                            className="w-24 p-1 border rounded text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold">
                          {member.dueAmount.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                    {/* Final Row: Total Values */}
                    <tr className="bg-gray-100 font-bold">
                      <td className="px-6 py-4 whitespace-nowrap text-sm"></td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        TOTAL
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {members.reduce((sum, m) => sum + m.totalMeals, 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {members
                          .reduce((sum, m) => sum + m.mealCost, 0)
                          .toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {members
                          .reduce((sum, m) => sum + m.individualShare.gas, 0)
                          .toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {members
                          .reduce((sum, m) => sum + m.guestMealCost, 0)
                          .toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {members
                          .reduce((sum, m) => sum + m.individualShare.others, 0)
                          .toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {members
                          .reduce((sum, m) => sum + m.individualShare.wifi, 0)
                          .toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {members
                          .reduce(
                            (sum, m) => sum + m.individualShare.electric,
                            0
                          )
                          .toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {members
                          .reduce((sum, m) => sum + m.individualShare.maasi, 0)
                          .toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {members
                          .reduce((sum, m) => sum + m.totalAdvanced, 0)
                          .toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {members
                          .reduce((sum, m) => sum + m.dueAmount, 0)
                          .toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex justify-center mt-6 space-x-4">
              <button
                onClick={downloadPdf}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg transition duration-300 ease-in-out text-lg transform hover:scale-105"
              >
                Download PDF
              </button>
              <button
                onClick={downloadExcel}
                className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg transition duration-300 ease-in-out text-lg transform hover:scale-105"
              >
                Download as Excel
              </button>
              <button
                onClick={() => setCurrentStep(1)} // Changed to go to Step 1 for full edit
                className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg transition duration-300 ease-in-out text-lg transform hover:scale-105"
              >
                Edit All Inputs
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-100 to-purple-200 flex flex-col items-center py-10 font-sans">
      <div className="w-full max-w-4xl bg-white p-8 rounded-2xl shadow-xl space-y-8">
        <h1 className="text-5xl font-extrabold text-center text-indigo-800 mb-8 tracking-tight">
          Meal & Expense Tracker
        </h1>

        <div className="relative mb-8">
          <div className="flex items-center justify-between mx-auto w-full max-w-2xl">
            {Array.from({ length: 7 }, (_, i) => i + 1).map((step) => (
              <div key={step} className="flex flex-col items-center relative">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl
                    ${step <= currentStep ? 'bg-indigo-600' : 'bg-gray-400'}
                    ${step === currentStep ? 'ring-4 ring-indigo-300' : ''}`}
                >
                  {step}
                </div>
                {step < 7 && (
                  <div
                    className={`absolute top-1/2 -right-20 w-20 h-1 bg-gray-300 transform -translate-y-1/2 ${
                      step < currentStep ? 'bg-indigo-400' : ''
                    }`}
                  ></div>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between w-full max-w-2xl mx-auto mt-2">
            <span className="text-xs text-gray-600">Members</span>
            <span className="text-xs text-gray-600">Names</span>
            <span className="text-xs text-gray-600">Details</span>
            <span className="text-xs text-gray-600">Guests</span>
            <span className="text-xs text-gray-600">Bazar</span>
            <span className="text-xs text-gray-600">Other Costs</span>
            <span className="text-xs text-gray-600">Summary</span>
          </div>
        </div>

        {renderStep()}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8 p-4 bg-gray-50 rounded-xl shadow-inner">
          <button
            onClick={prevStep}
            disabled={currentStep === 1 || showSummaryTable}
            className={`py-3 px-6 rounded-lg text-lg font-semibold transition duration-300 ease-in-out transform hover:scale-105
              ${
                currentStep === 1 || showSummaryTable
                  ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                  : 'bg-indigo-500 hover:bg-indigo-600 text-white shadow-md'
              }`}
          >
            Previous
          </button>
          <button
            onClick={nextStep}
            disabled={currentStep === 7} // Disable next button on summary page
            className={`py-3 px-6 rounded-lg text-lg font-semibold transition duration-300 ease-in-out transform hover:scale-105
              ${
                currentStep === 7
                  ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-md'
              }`}
          >
            Next
          </button>
        </div>

        {/* Custom Dialog */}
        {showDialog && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-8 rounded-lg shadow-2xl max-w-sm w-full text-center space-y-4 transform scale-100 transition-transform duration-300">
              <p className="text-xl font-semibold text-gray-800">
                {dialogMessage}
              </p>
              <button
                onClick={closeCustomDialog}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-5 rounded-lg shadow-md transition duration-300 ease-in-out"
              >
                OK
              </button>
            </div>
          </div>
        )}
      </div>

      <p className="mt-10 text-gray-600 text-sm font-medium">
        Developed with ❤️ by{' '}
        <span className="font-bold text-indigo-700">ANUP DHARA</span>
      </p>
    </div>
  );
};

export default App;
